<template>
    <div>
        <vue-headful :title="'[Contracts - '+contractCount+'] - AMOS365'" />

        <h4 class="page-header">
            Contracts ({{ contractCount }})

            <router-link to="/contracts/create" class="btn btn-sm btn-info" v-if="canManageContracts">
                <i class="fas fa-plus-square"></i> Create
            </router-link>
        </h4>

        <div class="card">
            <div class="card-body">
                <v-server-table ref="contractsTable" url="/contracts" :columns="columns" :options="options">

                    <div slot="beforeFilter" style="display: inline-block; margin-right: 5px;">

                    </div>

                    <div slot="afterFilter" class="ml-2">
                        <button class="btn btn-sm btn-default" v-on:click="clearFilters()" title="Clear All Filters">
                            <i class="fas fa-ban"></i>
                        </button>
                        <button class="btn btn-sm btn-default" v-on:click="showFilters = !showFilters" title="Apply Filters">
                            <i class="fas fa-filter"></i>
                            <span class="badge badge-danger" v-if="filterCount > 0">{{ filterCount }}</span>
                        </button>
                        <button type="button" class="btn btn-sm btn-default" v-on:click="exportTable()" :disabled="exportingTable">
                            <template v-if="!exportingTable"><i class="fa fa-file-excel"></i> Export</template>
                            <template v-else><i class="fa fa-spinner fa-spin"></i> Exporting</template>
                        </button>
                    </div>

                    <template slot="id" slot-scope="props">
                        <router-link :to="`/contracts/view/${props.row.id}`" title="View Contract" class="btn btn-sm btn-default">View</router-link>
                    </template>

                    <template slot="product_name" slot-scope="props">
                        <router-link :to="`/contracts/view/${props.row.id}`" title="View Contract" :class="{'text-muted': (props.row.is_active === 0)}">{{ props.row.product_name }}</router-link>
                    </template>

                    <template slot="agent_last_name" slot-scope="props">
                        <router-link :to="`/agents/view/${props.row.agent_id}`">{{ props.row.agent_last_name }}, {{ props.row.agent_first_name }}</router-link>
                    </template>

                    <template slot="client_last_name" slot-scope="props">
                        <router-link :to="`/clients/view/${props.row.client_id}`">{{ props.row.client_last_name }}, {{ props.row.client_first_name }}</router-link>
                    </template>

                    <template slot="writing_nbr" slot-scope="props">
                        <router-link :to="`/contracts/view/${props.row.id}`" title="View Contract">{{ props.row.writing_nbr }}</router-link>
                    </template>

                    <template slot="product_level_name" slot-scope="props">
                        <span v-if="props.row.product_level_name">
                            {{ props.row.product_level_name }}
                        </span>
                        <span v-else-if="props.row.commission">
                            <i class="fa fa-xs fa-flag text-warning text-muted" title="This is the Plain Text Commission Value from Legacy AMOS v1"></i> - {{ props.row.commission }}
                        </span>
                    </template>

                    <template slot="is_active" slot-scope="props">
                        <label class="badge badge-success" v-if="props.row.is_active == 1">Active</label>
                        <label class="badge badge-warning" v-else>Inactive</label>
                    </template>

                    <template slot="contract_status_id" slot-scope="props">
                        <label :class="'badge badge-'+props.row.contract_status_color">{{ props.row.contract_status_name }}</label>
                    </template>

                    <template slot="credentialing_date" slot-scope="props">
                        <span v-if="props.row.credentialing_date">{{ props.row.credentialing_date | moment("MM/DD/YYYY") }}</span>
                    </template>

                    <template slot="certification_date" slot-scope="props">
                        <span v-if="props.row.certification_date">{{ props.row.certification_date | moment("MM/DD/YYYY") }}</span>
                    </template>

                    <template slot="date_of_change" slot-scope="props">
                        <span v-if="props.row.date_of_change">{{ props.row.date_of_change | moment("MM/DD/YYYY") }}</span>
                    </template>

                    <template slot="requested_date" slot-scope="props">
                        <span v-if="props.row.requested_date">{{ props.row.requested_date | moment("MM/DD/YYYY") }}</span>
                    </template>

                    <template slot="sent_date" slot-scope="props">
                        <span v-if="props.row.sent_date">{{ props.row.sent_date | moment("MM/DD/YYYY") }}</span>
                    </template>

                    <template slot="termination_date" slot-scope="props">
                        <span v-if="props.row.termination_date">{{ props.row.termination_date | moment("MM/DD/YYYY") }}</span>
                    </template>

                    <template slot="created_at" slot-scope="props">
                        <span v-if="props.row.created_at">{{ props.row.created_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                    </template>

                    <template slot="updated_at" slot-scope="props">
                        <span v-if="props.row.updated_at">{{ props.row.updated_at | moment('timezone', userTimezone, "MM/DD/YYYY hh:mm a") }}</span>
                    </template>
                </v-server-table>
            </div>
        </div>

        <div v-show="loading" id="loading-table" class="alert alert-warning" style="width: 200px; text-align: center; position: fixed; top: 48%; left: 48%;">
            Loading Contracts <i class="fa fa-spinner fa-spin"></i>
        </div>

        <b-modal v-model="showFilters" :ref="'contractListFilters'" title="Agent Filters" size="lg" no-fade ok-title="Apply Filters" @ok="applyFilters()" :static="true">
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Agent Seller:</div>
                <div class="col-md-7 col-12">
                    <agent-typeahead :forFilters="true" :multiple="true" @agentSelected="setFilterValue('product_contracts.agent_seller_id', ...arguments)" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Agency Seller:</div>
                <div class="col-md-7 col-12">
                    <agency-typeahead :forFilters="true" :multiple="true" @agencySelected="setFilterValue('product_contracts.agency_seller_id', ...arguments)" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Agent Manager:</div>
                <div class="col-md-7 col-12">
                    <agent-typeahead :forFilters="true" :multiple="true" @agentSelected="setFilterValue('product_contracts.agent_manager_id', ...arguments)" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Agency Manager:</div>
                <div class="col-md-7 col-12">
                    <agency-typeahead :forFilters="true" :multiple="true" @agencySelected="setFilterValue('product_contracts.agency_manager_id', ...arguments)" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Product:</div>
                <div class="col-md-7 col-12">
                    <v-select v-model="filters['product_contracts.product_id']" :options="products" :reduce="product => product.id" label="product_name" placeholder="-- Select a Product --" multiple />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Product Type:</div>
                <div class="col-md-7 col-12">
                    <v-select v-model="filters['products.product_type_id']" :options="productTypes" :reduce="type => type.id" label="product_type_name" placeholder="-- Select a Product Type --" multiple />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Created Date:</div>
                <div class="col-md-7 col-12">
                    <date-filter :filter="filters['product_contracts.created_at']" size="sm" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Last Updated Date:</div>
                <div class="col-md-7 col-12">
                    <date-filter :filter="filters['product_contracts.updated_at']" size="sm" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Date of Change:</div>
                <div class="col-md-7 col-12">
                    <date-filter :filter="filters['product_contract_changes.date_of_change']" size="sm" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Requested Date:</div>
                <div class="col-md-7 col-12">
                    <date-filter :filter="filters['product_contracts.requested_date']" size="sm" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Credentialing Date:</div>
                <div class="col-md-7 col-12">
                    <date-filter :filter="filters['product_contracts.credentialing_date']" size="sm" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Certification Date:</div>
                <div class="col-md-7 col-12">
                    <date-filter :filter="filters['product_contracts.certification_date']" size="sm" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Sent Date:</div>
                <div class="col-md-7 col-12">
                    <date-filter :filter="filters['product_contracts.sent_date']" size="sm" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Termination Date:</div>
                <div class="col-md-7 col-12">
                    <date-filter :filter="filters['product_contracts.termination_date']" size="sm" />
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-3 col-12 md-md-0 mb-2 text-md-right text-muted">Contract Status:</div>
                <div class="col-md-7 col-12">
                    <v-select v-model="filters['product_contracts.contract_status_id']" :options="statuses" :reduce="status => status.id" label="contract_status_name" placeholder="-- Select a Contract Status --" multiple />
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    export default {
        data() {
            return {
                exportingTable: false,
                contractCount: null,
                showFilters: false,
                loadFilters: true,
                loading: false,
                userTimezone: this.$root.timezone,
                errors: {},
                columns: [
                    'id',
                    'product_name',
                    'product_level_name',
                    'seller_name',
                    'seller_npn',
                    'manager_name',
                    'manager_npn',
                    'marketer',
                    //'commission',
                    'writing_nbr',
                    'is_active',
                    'contract_status_id',
                    'date_of_change',
                    'requested_date',
                    'credentialing_date',
                    'certification_date',
                    'sent_date',
                    'termination_date',
                    'created_at',
                    'updated_at',
                ],
                options: {
                    sortable: [
                        'product_name',
                        'product_level_name',
                        'seller_name',
                        'manager_name',
                        'marketer',
                        'contract_status_id',
                        'date_of_change',
                        'requested_date',
                        'credentialing_date',
                        'certification_date',
                        'sent_date',
                        'termination_date',
                        'created_at',
                        'updated_at',
                    ],
                    headings: {
                        id: 'Actions',
                        product_name: 'Product',
                        product_level_name: 'Level',
                        seller_name: 'Seller',
                        seller_npn: 'Seller NPN',
                        manager_name: 'Manager',
                        manager_npn: 'Manager NPN',
                        commission: 'Commission',
                        writing_nbr: 'Writing#',
                        is_active: 'Active',
                        contract_status_id: 'Status',
                        date_of_change: 'Date of Change',
                        requested_date: 'Requested',
                        credentialing_date: 'Credentialing',
                        certification_date: 'Certification',
                        sent_date: 'Sent',
                        termination_date: 'Termination',
                        created_at: 'Date Created',
                        updated_at: 'Last Updated',
                    },
                    orderBy: {
                        ascending: false,
                        column: 'product_contracts.updated_at',
                    },
                    texts:{
                        filterPlaceholder:"Search Product Contracts",
                    },
                    responseAdapter: (response) => {
                        //Turn on the helper
                        this.loading = false;

                        //Set the count
                        this.contractCount = response.data.count;

                        //Check for filters
                        if(response.data.filters)
                        {
                            //Set the filters
                            this.setFilters(response.data.filters);
                        }

                        //Send it
                        return response.data;
                    },
                    requestAdapter: (data) => {
                        //Turn on the helper
                        this.loading = true;

                        //Load the Filters
                        if(this.loadFilters)
                        {
                            //Append it
                            data.load_filters = true;

                            //Cancel it
                            this.loadFilters = false;
                        }

                        //Add the type
                        data.filters = this.filters;

                        //Send it
                        return data;
                    },
                    uniqueKey: 'id',
                },

                filters: {
                    'product_contracts.agent_seller_id': [],
                    'product_contracts.agency_seller_id': [],
                    'product_contracts.agent_manager_id': [],
                    'product_contracts.agency_manager_id': [],
                    'product_contracts.writing_nbr': null,
                    'product_contracts.product_id': [],
                    'products.product_type_id': [],
                    'product_contracts.contract_status_id': [],
                    'product_contracts.created_at': {
                        'logic': null,
                        'value': null,
                        'value2': null,
                    },
                    'product_contracts.updated_at': {
                        'logic': null,
                        'value': null,
                        'value2': null,
                    },
                    'product_contract_changes.date_of_change': {
                        'logic': null,
                        'value': null,
                        'value2': null,
                    },
                    'product_contracts.requested_date': {
                        'logic': null,
                        'value': null,
                        'value2': null,
                    },
                    'product_contracts.credentialing_date': {
                        'logic': null,
                        'value': null,
                        'value2': null,
                    },
                    'product_contracts.certification_date': {
                        'logic': null,
                        'value': null,
                        'value2': null,
                    },
                    'product_contracts.sent_date': {
                        'logic': null,
                        'value': null,
                        'value2': null,
                    },
                    'product_contracts.termination_date': {
                        'logic': null,
                        'value': null,
                        'value2': null,
                    }
                },

                products: [],
                productTypes: [],
                statuses: [],
            }
        },

        watch: {

        },

        computed: {

            canManageContracts(){
                //Check Permissions
                return this.$root.hasPermission("contracts.manage");
            },

            filterCount(){
                //Get the keys
                var keys = Object.keys(this.filters);
                var count = 0;

                //Loop
                for (var i = keys.length - 1; i >= 0; i--)
                {
                    //Check the value
                    if(typeof this.filters[keys[i]] === 'object' && !Array.isArray(this.filters[keys[i]]) && this.filters[keys[i]] !== null)
                    {
                        //Check the Logic
                        if(this.filters[keys[i]]['logic'] != null)
                        {
                            count++;
                        }
                    }
                    else if(Array.isArray(this.filters[keys[i]]) && this.filters[keys[i]].length > 0)
                    {
                        count++;
                    }
                    else if(this.filters[keys[i]] != null && this.filters[keys[i]] != '')
                    {
                        count++;
                    }
                }

                //Send it
                return count;
            },
        },

        methods: {
            setFilters(data){
                this.products     = data.products;
                this.productTypes = data.productTypes;
                this.statuses     = data.statuses;

                if(data.defaults)
                {
                    const defaults = data.defaults;

                    const arrayKeys = [
                        'product_contracts.product_id',
                        'products.product_type_id',
                        'product_contracts.contract_status_id',
                    ];

                    arrayKeys.forEach(key => {
                        if(defaults[key] && defaults[key].length > 0)
                        {
                            this.filters[key] = defaults[key];
                        }
                    });

                    const dateKeys = [
                        'product_contracts.created_at',
                        'product_contracts.updated_at',
                        'product_contract_changes.date_of_change',
                        'product_contracts.requested_date',
                        'product_contracts.credentialing_date',
                        'product_contracts.certification_date',
                        'product_contracts.sent_date',
                        'product_contracts.termination_date',
                    ];

                    dateKeys.forEach(key => {
                        if(defaults[key])
                        {
                            this.filters[key].logic = defaults[key];
                        }
                    });
                }
            },

            setFilterValue(filter_key, value){
                //Handle the Key
                switch(filter_key)
                {
                    case 'product_contracts.agent_seller_id':
                    case 'product_contracts.agency_seller_id':
                    case 'product_contracts.agent_manager_id':
                    case 'product_contracts.agency_manager_id':
                        //Loop through the values and set
                        if(!Array.isArray)
                        {
                            value = [value];
                        }

                        //Process
                        var ids = [];

                        //Loop and Set
                        for (var i = 0; i < value.length; i++)
                        {
                            ids.push(value[i].id);
                        }

                        //Set it
                        this.filters[filter_key] = ids;
                        break;

                    default:
                        //Set the raw value
                        this.filters[filter_key] = value;
                        break;
                }
            },

            clearFilters(){
                //Get the keys
                var keys = Object.keys(this.filters);
                var count = 0;

                //Loop
                for (var i = keys.length - 1; i >= 0; i--)
                {
                    //Check the value
                    if(typeof this.filters[keys[i]] === 'object' && !Array.isArray(this.filters[keys[i]]) && this.filters[keys[i]] !== null)
                    {
                        this.filters[keys[i]] = {
                            'logic': null,
                            'value': null,
                            'value2': null,
                        };
                    }
                    else if(Array.isArray(this.filters[keys[i]]))
                    {
                        this.filters[keys[i]] = [];
                    }
                    else
                    {
                        this.filters[keys[i]] = null;
                    }
                }

                //Refresh
                this.$refs.contractsTable.refresh();
            },

            applyFilters(){
                //Apply the filters
                this.$refs.contractsTable.refresh();
            },

            exportTable(){
                //Loading indicator
                this.exportingTable = true;

                //Build the Filters
                //var filters = filterExtractor.extractFilters(this.filters);

                //Setup Columns
                var cols = JSON.parse(JSON.stringify(this.$refs.contractsTable.columns));

                //Build the payload
                var payload = {
                    filters: this.filters,
                    query: (this.$refs.contractsTable.getRequestParams()).query,
                    orderBy: this.$refs.contractsTable.options.orderBy.column,
                    ascending: this.$refs.contractsTable.options.orderBy.ascending,
                    table: 'contractsTable',
                    columns: cols,
                    export: true,
                };

                //Send it
                this.$http.get('/contracts', payload)
                    .then(response => {
                        //Open the new window to Download the File
                        if(response.data.queued)
                        {
                            //Alert the User
                            this.$toastr.s(response.data.message);
                        }
                        else
                        {
                            //Open the download URL
                            window.open(response.data.url);
                        }
                    })
                    .catch(err => {
                        //Alert the user
                        this.$toastr.e(JSON.stringify(err), 'Failed to Export');
                    })
                    .then(response => {
                        //reset
                        this.exportingTable = false;
                    });
            },
        },

        mounted() {
            //Focus on the SEarch Input
            this.$refs.contractsTable.$refs.genericFilter.focus();
        }
    }
</script>
